import { z } from 'zod';
import { papelSchema, papelInternoSchema } from './papeis';
import { recursoSchema, planoCodigoSchema } from './planos';

export const emailSchema = z.string().trim().toLowerCase().email().max(254);

/** Política de senha (ASVS 2.1): mínimo 12 caracteres, máximo 128. */
export const senhaSchema = z.string().min(12, 'Use pelo menos 12 caracteres').max(128);

export const loginSchema = z.object({
  email: emailSchema,
  senha: z.string().min(1).max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** "Esqueci minha senha" */
export const esqueciSenhaSchema = z.object({ email: emailSchema });
export const redefinirSenhaSchema = z.object({
  token: z.string().min(20).max(200),
  senha: senhaSchema,
});
export type RedefinirSenhaInput = z.infer<typeof redefinirSenhaSchema>;

/** Trocar a senha estando logado (Ajustes › Minha conta). */
export const trocarSenhaSchema = z
  .object({
    senhaAtual: z.string().min(1, 'Informe a senha atual').max(128),
    novaSenha: senhaSchema,
  })
  .refine((d) => d.senhaAtual !== d.novaSenha, {
    message: 'A nova senha precisa ser diferente da atual',
    path: ['novaSenha'],
  });
export type TrocarSenhaInput = z.infer<typeof trocarSenhaSchema>;

export const codigoMfaSchema = z.string().regex(/^\d{6}$/, 'Código de 6 dígitos');

export const mfaVerificarSchema = z.object({
  tokenTemporario: z.string().min(10),
  codigo: codigoMfaSchema,
  /** "Confiar neste aparelho por 30 dias" */
  confiarDispositivo: z.boolean().optional(),
});
export type MfaVerificarInput = z.infer<typeof mfaVerificarSchema>;

export const filialResumoSchema = z.object({ id: z.string().uuid(), nome: z.string() });
export type FilialResumo = z.infer<typeof filialResumoSchema>;

export const sessaoUsuarioSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  email: z.string(),
  papel: papelSchema,
  tenant: z.object({
    id: z.string().uuid(),
    nome: z.string(),
    corPrimaria: z.string().nullable(),
    logoUrl: z.string().nullable(),
  }),
  filiais: z.array(filialResumoSchema),
  plano: planoCodigoSchema,
  recursos: z.array(recursoSchema),
  suporte: z.object({ ativo: z.boolean(), expiraEm: z.string().nullable() }),
});
export type SessaoUsuario = z.infer<typeof sessaoUsuarioSchema>;

export type RespostaLogin =
  | { etapa: 'concluido'; accessToken: string; usuario: SessaoUsuario }
  | { etapa: 'mfa'; tokenTemporario: string }
  | { etapa: 'cadastrar_mfa'; tokenTemporario: string; otpauthUrl: string };

export const adminSessaoSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  email: z.string(),
  papel: papelInternoSchema,
});
export type AdminSessao = z.infer<typeof adminSessaoSchema>;

/** Equipe interna do admin (quem trabalha com você). Parceiro comercial é cadastrado em Parceiros. */
export const PAPEIS_EQUIPE_INTERNA = ['administrador', 'financeiro_interno', 'suporte'] as const;
export const NOME_PAPEL_INTERNO: Record<string, string> = {
  administrador: 'Administrador',
  financeiro_interno: 'Financeiro',
  suporte: 'Suporte',
  parceiro_comercial: 'Parceiro comercial',
};
export const DESCRICAO_PAPEL_INTERNO: Record<string, string> = {
  administrador: 'Tudo, inclusive a equipe, as regras fiscais e o dinheiro.',
  financeiro_interno:
    'Lojas, cobrança, faturas e parceiros. Não mexe na equipe nem nas regras fiscais.',
  suporte: 'Lojas, chamados, ajuda, avisos e integrações. Não vê dinheiro.',
};
export const membroEquipeCriarSchema = z.object({
  nome: z.string().trim().min(2).max(80),
  email: z.string().trim().toLowerCase().email().max(120),
  papel: z.enum(PAPEIS_EQUIPE_INTERNA),
});
export const membroEquipeAlterarSchema = z
  .object({
    nome: z.string().trim().min(2).max(80).optional(),
    papel: z.enum(PAPEIS_EQUIPE_INTERNA).optional(),
    ativo: z.boolean().optional(),
  })
  .refine(
    (d) => d.nome !== undefined || d.papel !== undefined || d.ativo !== undefined,
    'Nada para mudar',
  );
export const aceitarConviteAdminSchema = z.object({ senha: senhaSchema });
export type MembroEquipeCriar = z.infer<typeof membroEquipeCriarSchema>;
export interface MembroEquipe {
  id: string;
  nome: string;
  email: string;
  papel: string;
  ativo: boolean;
  mfaAtivo: boolean;
  /** convite enviado e ainda não aceito */
  convitePendente: boolean;
  ultimoAcessoEm: string | null;
  criadoEm: string;
}
